import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type {
  MutationReference,
  MutationReferenceType,
} from '@agentselly/immodossier-shared';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { useRouter } from '@tanstack/react-router';
import {
  shouldAutoRetryAuth,
  clearAuthAutoRetryFlagIfEpisodeOver,
} from '~/helper/authAutoRetry.ts';

export function useBackendMutation<
  T extends MutationReference,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>(
  key: T,
  options?: UseMutationOptions<MutationReferenceType<T>, Error, TVariables>,
) {
  const router = useRouter();
  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const searchParams = new URLSearchParams();

      Object.entries(variables).forEach(
        ([
          paramKey,
          value,
        ]) => {
          searchParams.append(paramKey, String(value));
        },
      );

      const url = `/action${key}?${searchParams.toString()}`;

      const response = await fetchBackend(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error(
          'useBackendMutation Error',
          response.status,
          response.statusText,
          await response.text(),
        );

        if (response.status === 401) {
          // Auto-retry: if the session token wasn't propagated after Clerk OAuth,
          // a single page reload resolves it. Guard ensures at most ONE reload.
          if (shouldAutoRetryAuth(Date.now(), sessionStorage)) {
            window.location.reload();
            throw new Error('Mutation failed');
          }

          // Redirect to login page via router so the history stack is preserved
          void router.navigate({ to: '/auth/login', replace: true });
        }

        // 403 is intentionally NOT handled here: useBackendMutation is used for
        // non-critical mutations where a 403 falls into the generic error path
        // (throwing 'Mutation failed'). Auth auto-retry for 403 is only needed
        // in useLoaderQuery which drives page-level loaders.

        throw new Error('Mutation failed');
      }

      // Happy path: successful response — clear auto-retry flag only if the
      // current episode has expired. This prevents a parallel 401 loader from
      // having its flag cleared mid-episode, which was the root cause of the
      // infinite reload loop (max 1 reload per 10-minute episode).
      clearAuthAutoRetryFlagIfEpisodeOver(Date.now(), sessionStorage);

      return (await response.json()) as MutationReferenceType<T>;
    },
    ...options,
  });
}
